import getExpMessage from '@exp/components/localization/getExpMessage';
import SingleCheckoutTypes from '../singleCheckoutTypes';

const canShipTo = (
  shipment: SingleCheckoutTypes.BrandShipment,
  countryValue: string,
  subdivisionValue?: string,
) => shipment.shippingMethods.some((method) => (
  !method.restrictions?.countries?.length ||
  method.restrictions.countries.some((c) => (
    c.value === countryValue && (
      !subdivisionValue ||
      !c.subdivisions?.length ||
      c.subdivisions.some((s) => s === subdivisionValue)
    )
  ))
));

const deriveRestrictions = (review: SingleCheckoutTypes.ReviewApiResponse) => {
  const { brands, shippingRestrictions } = review;

  if (!shippingRestrictions?.countries?.length) {
    return shippingRestrictions;
  }

  const shipmentBrands = brands.reduce((acc, brand) => {
    brand.shipments.forEach((shipment) => {
      acc.set(shipment, (
        brand.shipments.length > 1
          ? getExpMessage({
            defaultMessage: '{brand} - Shipment {number}',
            messageKey: 'summary.multiShipment',
            namespace: 'messages/checkoutMessages',
            values: {
              brand: brand.orgName,
              number: brand.shipments.findIndex((s) => s === shipment) + 1,
            },
          })
          : brand.orgName
      ));
    });

    return acc;
  }, new Map<SingleCheckoutTypes.BrandShipment, string>());

  const shipments = Array.from(shipmentBrands.keys());

  const countries = shippingRestrictions.countries.map((country) => {
    const [compatible, incompatibilities] = shipments.reduce(([_compatible, _incompatibilities], shipment) => {
      const shipmentCompatible = canShipTo(shipment, country.value);

      return [
        _compatible && shipmentCompatible,
        shipmentCompatible ? _incompatibilities : [..._incompatibilities, shipment],
      ];
    }, [true, [] as SingleCheckoutTypes.BrandShipment[]]);

    const subdivisions = country.subdivisions?.map((subdivision) => {
      const incompatible = shipments.filter((s) => !canShipTo(s, country.value, subdivision.value));

      return {
        ...subdivision,
        compatible: !incompatible.length,
        incompatibilities: incompatible.map((s) => shipmentBrands.get(s)),
      };
    });

    return {
      ...country,
      compatible,
      incompatibilities: incompatibilities.map((s) => shipmentBrands.get(s)),
      subdivisions,
    };
  });

  return {
    ...shippingRestrictions,
    countries,
  };
};

export default deriveRestrictions;
